import React from 'react';
import { Modal } from '../Shared/Modal';
import { ui } from '../../../config/uiSizes';
import type { RollbackPlanResponse } from '../../../types/rollback';
import { toFriendlyName } from '../../../utils/formatters';

interface RollbackConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    migrationName: string;
    completedLabel: string;
    plan: RollbackPlanResponse | null;
    loading: boolean;
    onConfirm: () => void;
}

const RollbackConfirmModal: React.FC<RollbackConfirmModalProps> = ({
    isOpen,
    onClose,
    migrationName,
    completedLabel,
    plan,
    loading,
    onConfirm,
}) => {
    const [openSection, setOpenSection] = React.useState<'delete' | 'revert' | 'restore' | null>(null);

    const toggle = (section: 'delete' | 'revert' | 'restore') =>
        setOpenSection(prev => (prev === section ? null : section));

    const deleteResources = plan?.resources.filter(r => r.action === 'delete') ?? [];
    const revertResources = plan?.resources.filter(r => r.action === 'revert') ?? [];
    const restoreResources = plan?.resources.filter(r => r.action === 'restore') ?? [];

    return (
        <Modal
            isOpen={isOpen}
            variant="rollbackPreview"
            title="Confirm Automated Rollback"
            onClose={onClose}
            showConfirmButton={false}
            customButtons={
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className={`flex-1 ${ui.button.modalButton} rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold shadow transition hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className={`flex-1 ${ui.button.modalButton} rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center ${ui.gap.sm}`}
                    >
                        {loading && (
                            <svg className={`animate-spin ${ui.svg.sm}`} viewBox="0 0 24 24" aria-hidden="true">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                        )}
                        {loading ? 'Starting…' : 'Confirm & Start Rollback'}
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                {/* Migration reference card */}
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 px-4 py-3 flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-1 self-stretch rounded-full bg-blue-400 dark:bg-blue-500" />
                    <div>
                        <p className={`text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide ${ui.text.xs}`}>
                            Migration Name
                        </p>
                        <p className={`font-semibold text-gray-900 dark:text-gray-100 ${ui.text.subTitle}`}>
                            {migrationName}
                        </p>
                        {completedLabel && (
                            <p className={`text-gray-500 dark:text-gray-400 ${ui.text.xs}`}>
                                Completed {completedLabel}
                            </p>
                        )}
                    </div>
                </div>

                {/* Resource summary accordion */}
                {plan && (
                    <div className={`rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden ${ui.text.body}`}>
                        {/* Delete row */}
                        <div>
                            <button
                                type="button"
                                onClick={() => toggle('delete')}
                                disabled={plan.resourcesToDelete === 0}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                            >
                                <svg className={`shrink-0 ${ui.svg.sm} text-gray-400 transition-transform ${openSection === 'delete' ? 'rotate-90' : ''} ${plan.resourcesToDelete === 0 ? 'opacity-0' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 flex-1">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                                    <span>Resources to delete</span>
                                </div>
                                <span className="font-semibold text-red-600 dark:text-red-400">{plan.resourcesToDelete}</span>
                            </button>
                            {openSection === 'delete' && (
                                <ul className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 divide-y divide-gray-100 dark:divide-gray-700/60 max-h-40 overflow-y-auto">
                                    {deleteResources.map(r => (
                                        <li key={r.resourceAddress} className="flex items-center justify-between px-4 py-2 gap-3">
                                            <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{r.resourceName}</span>
                                            <span className={`shrink-0 ${ui.text.xs} text-gray-500 dark:text-gray-400`}>{toFriendlyName(r.resourceType)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Revert row */}
                        <div>
                            <button
                                type="button"
                                onClick={() => toggle('revert')}
                                disabled={plan.resourcesToRevert === 0}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                            >
                                <svg className={`shrink-0 ${ui.svg.sm} text-gray-400 transition-transform ${openSection === 'revert' ? 'rotate-90' : ''} ${plan.resourcesToRevert === 0 ? 'opacity-0' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 flex-1">
                                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                                    <span>Resources to revert</span>
                                </div>
                                <span className="font-semibold text-amber-600 dark:text-amber-400">{plan.resourcesToRevert}</span>
                            </button>
                            {openSection === 'revert' && (
                                <ul className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 divide-y divide-gray-100 dark:divide-gray-700/60 max-h-40 overflow-y-auto">
                                    {revertResources.map(r => (
                                        <li key={r.resourceAddress} className="flex items-center justify-between px-4 py-2 gap-3">
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{r.resourceName}</p>
                                                {r.changedAttributes && r.changedAttributes.length > 0 && (
                                                    <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>{r.changedAttributes.length} propert{r.changedAttributes.length !== 1 ? 'ies' : 'y'} changed</p>
                                                )}
                                            </div>
                                            <span className={`shrink-0 ${ui.text.xs} text-gray-500 dark:text-gray-400`}>{toFriendlyName(r.resourceType)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Restore row */}
                        <div>
                            <button
                                type="button"
                                onClick={() => toggle('restore')}
                                disabled={plan.resourcesToRestore === 0}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                            >
                                <svg className={`shrink-0 ${ui.svg.sm} text-gray-400 transition-transform ${openSection === 'restore' ? 'rotate-90' : ''} ${plan.resourcesToRestore === 0 ? 'opacity-0' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 flex-1">
                                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
                                    <span>Resources to restore</span>
                                </div>
                                <span className="font-semibold text-green-600 dark:text-green-400">{plan.resourcesToRestore}</span>
                            </button>
                            {openSection === 'restore' && (
                                <ul className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 divide-y divide-gray-100 dark:divide-gray-700/60 max-h-40 overflow-y-auto">
                                    {restoreResources.map(r => (
                                        <li key={r.resourceAddress} className="flex items-center justify-between px-4 py-2 gap-3">
                                            <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{r.resourceName}</span>
                                            <span className={`shrink-0 ${ui.text.xs} text-gray-500 dark:text-gray-400`}>{toFriendlyName(r.resourceType)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* Yellow note banner */}
                <div className={`flex items-start gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 px-3 py-2.5 ${ui.text.body}`}>
                    <svg className={`flex-shrink-0 mt-0.5 ${ui.svg.sm} text-yellow-500 dark:text-yellow-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-yellow-800 dark:text-yellow-300">
                        Rollback will run in the background and may take several minutes to complete.
                    </p>
                </div>

                {/* Warning callout */}
                <div className="flex items-center gap-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 px-4 py-3">
                    <svg className={`flex-shrink-0 ${ui.svg.sm} text-red-500 dark:text-red-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className={`font-semibold text-red-600 dark:text-red-400 ${ui.text.body}`}>
                        This action cannot be automatically undone.
                    </p>
                </div>
            </div>
        </Modal>
    );
};

export default RollbackConfirmModal;
